import cssVar from "./cssVar";

/**
 * Shared ApexCharts configuration.
 *
 * Two jobs: keep every chart visually consistent, and be the single place that
 * translates runtime tokens into the literal colour strings Apex needs. A
 * component that reaches for a hex directly has bypassed the ramps.
 */

/** Categorical ramp — series identity. Never encodes rank or magnitude. */
export const seriesColors = (el, count = 8) =>
  Array.from({ length: count }, (unused, i) =>
    cssVar(`--cp-series-${i + 1}`, el, "#3953fb")
  );

/** Sequential ramp — magnitude, light to dark. Heatmaps, funnels. */
export const sequentialColors = (el, count = 6) =>
  Array.from({ length: count }, (unused, i) =>
    cssVar(`--cp-seq-${i + 1}`, el, "#3953fb")
  );

export const divergingColors = el => ({
  negative: cssVar("--cp-div-neg", el, "#ef4444"),
  mid: cssVar("--cp-div-mid", el, "#9ca3af"),
  positive: cssVar("--cp-div-pos", el, "#29b969")
});

/**
 * Base options every chart spreads first. Animations off: these render inside
 * a streaming thread, and a chart that animates on each re-render reads as a
 * glitch rather than as polish.
 */
/**
 * EMPHASIS colouring: one series or bar carries the point, the rest are
 * context in grey.
 *
 * This is the highest-value idiom in the whole set and the one most often
 * skipped. "Paid search earns 3x the next channel" is a sentence a reader gets
 * instantly from one blue bar among grey ones, and not at all from six bars in
 * six different colours — where the hues imply six unrelated categories and
 * the eye has nothing to land on.
 *
 * Rainbow assignment is the anti-pattern this replaces: never cycle hues by
 * position. Colour encodes meaning, and here the meaning is "this one".
 */
export const emphasisColors = (el, count, highlightIndex) => {
  const accent = cssVar("--cp-series-1", el, "#3953fb");
  const muted = cssVar("--cp-ink-3", el, "#9ca3af");
  return Array.from({ length: count }, (unused, i) =>
    i === highlightIndex ? accent : muted
  );
};

/** Index of the largest value — the default thing worth emphasising. */
export const largestIndex = values =>
  values.reduce((best, value, i) => (Number(value) > Number(values[best]) ? i : best), 0);

/**
 * Axis tick formatter. Raw axis numbers ("40000", "512000") are the fastest
 * way to make a chart look unfinished; thousands separators and compact
 * currency are what the reference dashboards do.
 */
export const axisFormatter = (unit = "") => value => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  if (unit === "%") return `${Math.round(num * 10) / 10}%`;
  if (unit === "currency") {
    if (Math.abs(num) >= 1000000) return `$${Math.round(num / 100000) / 10}M`;
    if (Math.abs(num) >= 1000) return `$${Math.round(num / 1000)}K`;
    return `$${Math.round(num)}`;
  }
  if (Math.abs(num) >= 1000) return num.toLocaleString();
  return String(Math.round(num * 10) / 10);
};

/**
 * Tooltip styled as a dark card, matching the reference. Apex's default is a
 * pale box that disappears against a light surface.
 */
export const tooltipTheme = { theme: "dark", style: { fontSize: "12px" } };

export const baseChartOptions = el => ({
  chart: {
    toolbar: { show: false },
    animations: { enabled: false },
    selection: { enabled: false },
    zoom: { enabled: false },
    fontFamily: "inherit",
    parentHeightOffset: 0
  },
  dataLabels: { enabled: false },
  legend: { show: false },
  grid: {
    borderColor: cssVar("--cp-border", el, "#e8eaf2"),
    strokeDashArray: 3,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } }
  },
  tooltip: tooltipTheme,
  states: {
    active: { filter: { type: "none" } }
  }
});

/** Axis label styling shared by every cartesian chart. */
export const axisLabelStyle = el => ({
  style: {
    colors: cssVar("--cp-ink-3", el, "#9ca3af"),
    fontSize: "11px"
  }
});
